import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api from '@/utils/api';
import { SmsOptInCheckbox } from './SmsConsentDisclosure';

const BookingModal = ({ isOpen, onClose, bookingType = 'consultation' }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    booking_type: bookingType,
    date: '',
    time: '',
    message: '',
    sms_opt_in: false,
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/bookings', formData);
      toast.success('Booking request submitted! We\'ll contact you soon.');
      onClose();
      setFormData({
        name: '',
        email: '',
        phone: '',
        booking_type: bookingType,
        date: '',
        time: '',
        message: ''
      });
    } catch (error) {
      toast.error('Failed to submit booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="booking-modal">
        <DialogHeader>
          <DialogTitle>Schedule an Appointment</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label htmlFor="name">Full Name</Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              data-testid="booking-name-input"
            />
          </div>
          
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              data-testid="booking-email-input"
            />
          </div>
          
          <div>
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleInputChange}
              required
              data-testid="booking-phone-input"
            />
            <SmsOptInCheckbox
              checked={formData.sms_opt_in}
              onChange={(v) => setFormData({ ...formData, sms_opt_in: v })}
            />
          </div>
          
          <div>
            <Label htmlFor="booking_type">Appointment Type</Label>
            <Select name="booking_type" value={formData.booking_type} onValueChange={(value) => setFormData({...formData, booking_type: value})}>
              <SelectTrigger data-testid="booking-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="consultation">Consultation</SelectItem>
                <SelectItem value="property_viewing">Property Viewing</SelectItem>
                <SelectItem value="valuation">Home Valuation</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="date">Preferred Date</Label>
              <Input
                id="date"
                name="date"
                type="date"
                value={formData.date}
                onChange={handleInputChange}
                required
                data-testid="booking-date-input"
              />
            </div>
            <div>
              <Label htmlFor="time">Preferred Time</Label>
              <Input
                id="time"
                name="time"
                type="time"
                value={formData.time}
                onChange={handleInputChange}
                required
                data-testid="booking-time-input"
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="message">Message (optional)</Label>
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              rows={3}
              data-testid="booking-message-input"
            />
          </div>
          
          <Button 
            type="submit" 
            className="w-full bg-[#BCA480] hover:bg-[#a89470]" 
            disabled={loading}
            data-testid="booking-submit-btn"
          >
            {loading ? 'Submitting...' : 'Request Appointment'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BookingModal;
